import { consentBootScript } from "@/lib/consent";
import { ConsentBanner } from "@/components/public/consent-banner";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { ENTITY_GRAPH } from "@/lib/structured-data/entity-graph";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "@/components/ui/sonner";
import { PublicShell } from "@/components/public/public-shell";
import { StatusBlock } from "@/components/route-boundaries";

function NotFoundComponent() {
  return (
    <PublicShell ground="bg-cream-200">
      <StatusBlock kind="404" />
    </PublicShell>
  );
}

function ErrorComponent({ error: rawError, reset }: ErrorComponentProps) {
  const error = rawError as Error;
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  // The root error replaces RootComponent, so the shell gets its own query provider here.
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <PublicShell ground="bg-cream-200">
        <StatusBlock kind="error" onRetry={() => { router.invalidate(); reset(); }} />
      </PublicShell>
    </QueryClientProvider>
  );
}

const SITE_TITLE = "د. سارة النحّاس — منصة التربية والعلاقة الآمنة";
const SITE_DESCRIPTION =
  "منصّة د. سارة النحّاس: استشارات تربوية فردية أونلاين أو في العيادة، مدوّنة أسبوعية، والبوصلة لاكتشاف التخصص الأقرب لك.";
const SITE_URL = "https://drsaranahas.com";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: SITE_TITLE },
      { name: "description", content: SITE_DESCRIPTION },
      { name: "author", content: "د. سارة النحّاس" },
      { property: "og:title", content: SITE_TITLE },
      { property: "og:description", content: SITE_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SITE_TITLE },
      { name: "twitter:description", content: SITE_DESCRIPTION },
      { name: "theme-color", content: "#EFE6DA" },
      { name: "google-site-verification", content: "aszC_yJRvMI5js6i3ElrcJn1UVbpXdcWtnx8Qj5UzeY" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(ENTITY_GRAPH) },
      // Google Analytics (gtag.js)
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-HEK40SRGNN",
        async: true,
      },
      {
        // Consent Mode v2 defaults run before GA config (see src/lib/consent.ts).
        children: consentBootScript("G-HEK40SRGNN"),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

/**
 * The platform is Arabic-first, but the Bousala flow can run in English via
 * `?lang=en`. Screen readers pick their voice from <html lang>, so it has to
 * follow the active language instead of being pinned to Arabic.
 */
function useDocumentLang(): { lang: "ar" | "en"; dir: "rtl" | "ltr" } {
  const isEnglish = useRouterState({
    select: (s) => (s.location.search as Record<string, unknown> | undefined)?.["lang"] === "en",
  });
  return isEnglish ? { lang: "en", dir: "ltr" } : { lang: "ar", dir: "rtl" };
}

function RootShell({ children }: { children: ReactNode }) {
  const { lang } = useDocumentLang();
  if (lang === "en") {
    return (
      <html lang="en" dir="ltr" suppressHydrationWarning>
        <head suppressHydrationWarning>
          <HeadContent />
        </head>
        <body suppressHydrationWarning>
          {children}
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <html lang="ar" dir="rtl" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const { dir } = useDocumentLang();

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => data.subscription.unsubscribe();
  }, [router, queryClient]);

  // Track SPA route changes with Google Analytics.
  useEffect(() => {
    const pathname = router.state.location.pathname;
    // @ts-expect-error gtag is injected by the gtag.js snippet.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      // @ts-expect-error gtag is injected by the gtag.js snippet.
      window.gtag("event", "page_view", { page_path: pathname });
    }
  }, [router.state.location.pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster dir={dir} />
      <ConsentBanner />
    </QueryClientProvider>
  );
}
